import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import FilterAltIcon from "@mui/icons-material/FilterAlt"
import { Box, Stack, Typography, Divider, useTheme, Fade } from "@mui/material"
import Grid from "@mui/material/Unstable_Grid2"
import { ReactElement, SyntheticEvent, useState } from "react"
import { useDispatch } from "react-redux"
import { useSearchParams } from "react-router-dom"

import SelectedFilterTab from "./selectedFilterTab"
import {
    APPLICATION_ID_KW,
    ASSIGNED_TO,
    DNO_DATE_FROM_KW,
    DNO_DATE_TO_KW,
    FILTER_PANEL_SLIDE_MS,
    STATUS,
    TECHNOLOGY,
    POSTCODE,
    ADDRESS,
    ESCALATED_REASON,
    INSTALLER_EMAIL,
    MIN_MAX_DEMAND,
    MAX_MAX_DEMAND,
    MIN_GEN_CAPACITY,
    MAX_GEN_CAPACITY,
    UPDATED_SINCE,
} from "../../constants/generalConstants"
import { useApplicationDeriveFilters } from "../../hooks/useApplicationsDeriveFiltersFromUrl"
import { useGetState } from "../../hooks/useGetState"
import { useScreenSize } from "../../hooks/useScreenSize"
import {
    getShowDnoApplicationFilters,
    setShowDnoApplicationFilters,
} from "../../redux/preferencesSlice"
import { GRID_SPACING } from "../../themes/defaultThemeParams"
import AppButton from "../buttons/appButton"
import HelpIcon from "../helper/helpIcon"
import RevalidatingSpinner from "../loading/spinners/revalidatingSpinner"
import AppPaper from "../surfaces/appPaper"
import DisplayIf from "../wrappers/displayIf"
import DisplayIfElse from "../wrappers/displayIfElse"

export const MAX_CAPACITY = 100
export const TOT_DEM_MAX_CAPACITY = 100

interface IFilterTabBarFrameProps {
    applicationId: string
    isValidating: boolean
    tooltip: string
    all: boolean
    heading?: string
    applications: string
    colour?: string
    isDnoMode: boolean
    clearLocalAddress: () => void
    clearLocalPostcode: () => void
    clearLocalInstallerEmail: () => void
    clearLocalGenCapacity: () => void
    clearLocalDemCapacity: () => void
}

const FilterFocusIcon = ({
    showFilters,
}: {
    showFilters: boolean
}): ReactElement => (showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />)

/**
 * Abstracts layout for the panel tabs showing selected filters for
 * list applications pages in web portal
 */
const DNOFilterPanelFrame = ({
    applicationId,
    isValidating,
    tooltip,
    all,
    heading: type,
    applications,
    colour,
    isDnoMode,
    clearLocalAddress,
    clearLocalPostcode,
    clearLocalInstallerEmail,
    clearLocalGenCapacity,
    clearLocalDemCapacity,
}: IFilterTabBarFrameProps): ReactElement => {
    const theme = useTheme()
    const dispatch = useDispatch()
    const showFilters = useGetState(getShowDnoApplicationFilters)
    const [filterButtonFocus, setFilterButtonFocus] = useState(false)
    const [searchParams, setSearchParams] = useSearchParams()
    const { isTabletOrMob } = useScreenSize()
    // Filter and sort hooks
    const {
        setFilterFromTextfield,
        clearAllFilters,
        dateFrom,
        dateTo,
        updatedSince,
        status,
        assignedTo,
        technology,
        postcode,
        escalatedReason,
        installerEmail,
        address,
        assignedToName,
        genCapacityMin,
        genCapacityMax,
        totDemCapacityMin,
        totDemCapacityMax,
        setFilterFromSlider,
    } = useApplicationDeriveFilters(searchParams, setSearchParams, isDnoMode)
    // params - this is to reduce renders
    const [, setLocalApplicationId] = useState<string>(applicationId)

    /**
     * Called when slider is moved is committed. Updates slider local state
     * but not URL params.
     * @param e_ - MUI slider event, not used
     * @param newValue - array of upper and lower slider value
     */
    /**
     * Clears all local state and URL search params
     */
    const clearAllLocalAndUrl = (): void => {
        setLocalApplicationId("")
        clearLocalPostcode()
        clearLocalAddress()
        clearLocalInstallerEmail()
        clearLocalGenCapacity()
        clearLocalDemCapacity()
        clearAllFilters()
    }

    return (
        <Grid xs={12}>
            <Stack
                direction="row"
                spacing={GRID_SPACING}
                alignItems="center"
                justifyContent="space-between"
            >
                <DisplayIfElse
                    condition={isTabletOrMob}
                    displayIfTrue={
                        <Typography color="text.primary" variant="h6">
                            {isDnoMode
                                ? `${type} Applications`
                                : "My Applications"}
                        </Typography>
                    }
                    displayIfFalse={
                        <AppPaper
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                paddingY: theme.spacing(1),
                            }}
                        >
                            <Typography variant="h6" component="div">
                                {all ? "All " : null}
                                <Box color={colour} display="inline">
                                    {type}{" "}
                                </Box>
                                {applications}
                            </Typography>
                            <HelpIcon tooltip={tooltip} />
                        </AppPaper>
                    }
                />

                <Stack
                    direction="row"
                    spacing={GRID_SPACING}
                    justifyContent="flex-end"
                    alignItems="center"
                >
                    <RevalidatingSpinner isValidating={isValidating} />
                    <DisplayIf condition={!isTabletOrMob}>
                        <>
                            <Stack
                                direction="row"
                                alignItems="center"
                                flexWrap="wrap"
                                gap={2}
                            >
                                <SelectedFilterTab
                                    name="Application"
                                    value={applicationId}
                                    onDelete={() => {
                                        setLocalApplicationId("")
                                        setFilterFromTextfield(
                                            "",
                                            APPLICATION_ID_KW
                                        )
                                    }}
                                />
                                <SelectedFilterTab
                                    name="Technology"
                                    value={technology}
                                    onDelete={() => {
                                        setFilterFromTextfield("", TECHNOLOGY)
                                    }}
                                />
                                <SelectedFilterTab
                                    name="Escalated Reason"
                                    value={escalatedReason}
                                    onDelete={() => {
                                        setFilterFromTextfield(
                                            "",
                                            ESCALATED_REASON
                                        )
                                    }}
                                />
                                <SelectedFilterTab
                                    name="Postcode"
                                    value={postcode}
                                    onDelete={() => {
                                        setFilterFromTextfield("", POSTCODE)
                                        clearLocalPostcode()
                                    }}
                                />
                                <SelectedFilterTab
                                    name="Address"
                                    value={address}
                                    onDelete={() => {
                                        setFilterFromTextfield("", ADDRESS)
                                        clearLocalAddress()
                                    }}
                                />
                                <SelectedFilterTab
                                    name="Installer Email"
                                    value={installerEmail}
                                    onDelete={() => {
                                        setFilterFromTextfield(
                                            "",
                                            INSTALLER_EMAIL
                                        )
                                        clearLocalInstallerEmail()
                                    }}
                                />
                                <SelectedFilterTab
                                    name="Date From"
                                    value={dateFrom}
                                    onDelete={() => {
                                        setFilterFromTextfield(
                                            "",
                                            DNO_DATE_FROM_KW
                                        )
                                    }}
                                />
                                <SelectedFilterTab
                                    name="Date To"
                                    value={dateTo}
                                    onDelete={() => {
                                        setFilterFromTextfield(
                                            "",
                                            DNO_DATE_TO_KW
                                        )
                                    }}
                                />
                                <SelectedFilterTab
                                    name="Last Updated"
                                    value={updatedSince}
                                    onDelete={() => {
                                        setFilterFromTextfield(
                                            "",
                                            UPDATED_SINCE
                                        )
                                    }}
                                />
                                <SelectedFilterTab
                                    name="Status"
                                    value={status}
                                    onDelete={() => {
                                        setFilterFromTextfield("", STATUS)
                                    }}
                                />
                                <SelectedFilterTab
                                    name="Assigned to"
                                    value={assignedToName}
                                    onDelete={() => {
                                        setFilterFromTextfield("", ASSIGNED_TO)
                                    }}
                                />
                                <SelectedFilterTab
                                    name="Min Capacity"
                                    value={genCapacityMin}
                                    show={genCapacityMin !== 0}
                                    onDelete={() => {
                                        setFilterFromSlider(
                                            {} as SyntheticEvent<
                                                Element,
                                                Event
                                            >,
                                            [0],
                                            [MIN_GEN_CAPACITY]
                                        )
                                    }}
                                />
                                <SelectedFilterTab
                                    name="Max Capacity"
                                    value={genCapacityMax}
                                    show={genCapacityMax !== MAX_CAPACITY}
                                    onDelete={() => {
                                        setFilterFromSlider(
                                            {} as SyntheticEvent<
                                                Element,
                                                Event
                                            >,
                                            [MAX_CAPACITY],
                                            [MAX_GEN_CAPACITY]
                                        )
                                    }}
                                />
                                <SelectedFilterTab
                                    name="Demand Min Capacity"
                                    value={totDemCapacityMin}
                                    show={totDemCapacityMin !== 0}
                                    onDelete={() => {
                                        setFilterFromSlider(
                                            {} as SyntheticEvent<
                                                Element,
                                                Event
                                            >,
                                            [0],
                                            [MIN_MAX_DEMAND]
                                        )
                                    }}
                                />
                                <SelectedFilterTab
                                    name="Demand Max Capacity"
                                    value={totDemCapacityMax}
                                    show={
                                        totDemCapacityMax !==
                                        TOT_DEM_MAX_CAPACITY
                                    }
                                    onDelete={() => {
                                        setFilterFromSlider(
                                            {} as SyntheticEvent<
                                                Element,
                                                Event
                                            >,
                                            [TOT_DEM_MAX_CAPACITY],
                                            [MAX_MAX_DEMAND]
                                        )
                                    }}
                                />
                                <Fade
                                    in={
                                        !!applicationId ||
                                        !!dateFrom ||
                                        !!technology ||
                                        !!postcode ||
                                        !!address ||
                                        !!escalatedReason ||
                                        !!installerEmail ||
                                        !!dateTo ||
                                        !!updatedSince ||
                                        !!genCapacityMin ||
                                        !!genCapacityMax ||
                                        !!totDemCapacityMin ||
                                        !!totDemCapacityMax ||
                                        !!status ||
                                        !!assignedTo
                                    }
                                    unmountOnExit
                                    timeout={FILTER_PANEL_SLIDE_MS}
                                >
                                    <AppButton
                                        variant="outlined"
                                        onClick={clearAllLocalAndUrl}
                                        endIcon={<ClearOutlinedIcon />}
                                    >
                                        Clear All
                                    </AppButton>
                                </Fade>
                            </Stack>
                            <Divider orientation="vertical" flexItem />
                        </>
                    </DisplayIf>
                    <AppButton
                        onClick={() => dispatch(setShowDnoApplicationFilters())}
                        endIcon={
                            filterButtonFocus ? (
                                <FilterFocusIcon showFilters={showFilters} />
                            ) : (
                                <FilterAltIcon />
                            )
                        }
                        color="secondary"
                        onMouseEnter={() => setFilterButtonFocus(true)}
                        onMouseLeave={() => setFilterButtonFocus(false)}
                    >
                        Filters
                    </AppButton>
                </Stack>
            </Stack>
        </Grid>
    )
}

DNOFilterPanelFrame.defaultProps = {
    heading: "",
    colour: "",
}

export default DNOFilterPanelFrame
